import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CloudOff, Loader2, Receipt, Wallet } from "lucide-react";
import MarketDetailOrderModal from "../components/bond-predict/detail/MarketDetailOrderModal";
import PortfolioCancelModal from "../components/bond-predict/portfolio/PortfolioCancelModal";
import PortfolioChrome from "../components/bond-predict/portfolio/PortfolioChrome";
import PortfolioFiltersRail from "../components/bond-predict/portfolio/PortfolioFiltersRail";
import PortfolioHoldingsTable from "../components/bond-predict/portfolio/PortfolioHoldingsTable";
import PortfolioOrdersTable from "../components/bond-predict/portfolio/PortfolioOrdersTable";
import { selectAppUser, checkAppAuth } from "../redux/appAuthSlice";
import {
  cancelBondPredictOrder,
  fetchBondPredictOrders,
  fetchBondPredictPortfolio,
  selectBondPredictCancelOrderBusyId,
  selectBondPredictOrderBook,
  selectBondPredictOrderBookStatus,
  selectBondPredictPortfolioError,
  selectBondPredictPortfolioOrders,
  selectBondPredictPortfolioOrdersStatus,
  selectBondPredictPortfolioPositions,
  selectBondPredictPortfolioStatus,
  selectBondPredictPortfolioSummary,
} from "../redux/bondPredictSlice";
import {
  applyHoldingsFilters,
  applyOrdersFilters,
  buildCancelPreview,
  collectPortfolioCategories,
  countPortfolioFiltersActive,
  DEFAULT_HOLDINGS_FILTERS,
  DEFAULT_ORDERS_FILTERS,
  isOpenOrder,
  sortOrders,
  sortPositions,
} from "../utils/bondPredictPortfolioHelpers";
import {
  ORDER_SIDES,
  resolvePortfolioTradeTarget,
} from "../utils/bondPredictTradeHelpers";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import "../bond-predict.css";
import "../bond-predict-portfolio.css";
import "../bond-predict-trade-modal.css";

const LIST_PATH = "/app/bond-predict";
const FETCH_LIMIT = 50;
const FILTER_RAIL_DEFAULT = 248;
const FILTER_RAIL_MIN = 68;
const FILTER_RAIL_MAX = 380;
const FILTER_RAIL_COMPACT = 120;

const BondPredictPortfolio = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const orderBook = useSelector(selectBondPredictOrderBook);
  const orderBookStatus = useSelector(selectBondPredictOrderBookStatus);

  const summary = useSelector(selectBondPredictPortfolioSummary);
  const positions = useSelector(selectBondPredictPortfolioPositions);
  const portfolioStatus = useSelector(selectBondPredictPortfolioStatus);
  const portfolioError = useSelector(selectBondPredictPortfolioError);
  const orders = useSelector(selectBondPredictPortfolioOrders);
  const ordersStatus = useSelector(selectBondPredictPortfolioOrdersStatus);
  const cancelBusyId = useSelector(selectBondPredictCancelOrderBusyId);

  const [activeTab, setActiveTab] = useState("holdings");
  const [holdingsFilters, setHoldingsFilters] = useState(DEFAULT_HOLDINGS_FILTERS);
  const [ordersFilters, setOrdersFilters] = useState(DEFAULT_ORDERS_FILTERS);
  const [search, setSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState(null);
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [tradeInitialSide, setTradeInitialSide] = useState(ORDER_SIDES.BUY);
  const [tradeMarket, setTradeMarket] = useState(null);
  const [tradeOutcome, setTradeOutcome] = useState(null);
  const {
    size: filterRailWidth,
    setClampedSize: setFilterRailWidth,
    reset: resetFilterRailWidth,
    minSize: filterRailMin,
    maxSize: filterRailMax,
  } = usePersistedPanelSize("bb-bp-portfolio-filter-rail-w", {
    defaultSize: FILTER_RAIL_DEFAULT,
    minSize: FILTER_RAIL_MIN,
    maxSize: FILTER_RAIL_MAX,
  });
  const isFilterRailCollapsed = filterRailWidth <= FILTER_RAIL_COMPACT;

  const toggleFilterRailWidth = useCallback(() => {
    if (isFilterRailCollapsed) resetFilterRailWidth();
    else setFilterRailWidth(FILTER_RAIL_MIN);
  }, [isFilterRailCollapsed, resetFilterRailWidth, setFilterRailWidth]);

  const handleFilterRailResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = filterRailWidth;
      document.body.classList.add("ws-is-resizing");

      const onMove = (ev) => {
        setFilterRailWidth(startSize + (ev.clientX - startX));
      };

      const onUp = () => {
        document.body.classList.remove("ws-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [filterRailWidth, setFilterRailWidth]
  );

  const load = useCallback(async () => {
    await Promise.all([
      dispatch(fetchBondPredictPortfolio({ limit: FETCH_LIMIT, skip: 0 })),
      dispatch(fetchBondPredictOrders({ limit: FETCH_LIMIT, skip: 0 })),
    ]);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const sortedPositions = useMemo(() => sortPositions(positions), [positions]);
  const sortedOrders = useMemo(() => sortOrders(orders), [orders]);
  const openOrdersCount = useMemo(
    () => sortedOrders.filter(isOpenOrder).length,
    [sortedOrders]
  );

  const categoryOptions = useMemo(() => {
    const source = activeTab === "holdings" ? sortedPositions : sortedOrders;
    return collectPortfolioCategories(source);
  }, [activeTab, sortedPositions, sortedOrders]);

  const filteredPositions = useMemo(
    () => applyHoldingsFilters(sortedPositions, holdingsFilters, search),
    [sortedPositions, holdingsFilters, search]
  );

  const filteredOrders = useMemo(
    () => applyOrdersFilters(sortedOrders, ordersFilters, search),
    [sortedOrders, ordersFilters, search]
  );

  const cancelPreview = useMemo(() => buildCancelPreview(orderToCancel), [orderToCancel]);

  const pnl = Number(summary?.totalRealizedPnL || 0);
  const invested = Number(summary?.totalInvested || 0);
  const pnlTone = pnl > 0 ? "gain" : pnl < 0 ? "loss" : "flat";

  const activeFilterCount = useMemo(
    () => countPortfolioFiltersActive(activeTab, holdingsFilters, ordersFilters, search),
    [activeTab, holdingsFilters, ordersFilters, search]
  );

  const handleClearFilters = () => {
    setSearch("");
    if (activeTab === "holdings") {
      setHoldingsFilters(DEFAULT_HOLDINGS_FILTERS);
    } else {
      setOrdersFilters(DEFAULT_ORDERS_FILTERS);
    }
  };

  const positionKey = (position) => {
    const marketId = position?.market?._id || position?.market;
    return String(position?._id || `${marketId}-${position?.outcome?._id || position?.outcome}`);
  };

  useEffect(() => {
    const list = activeTab === "holdings" ? filteredPositions : filteredOrders;
    if (!expandedKey) return;
    const stillVisible =
      activeTab === "holdings"
        ? list.some((item) => positionKey(item) === expandedKey)
        : list.some((item) => String(item._id) === expandedKey);
    if (!stillVisible) {
      setExpandedKey(null);
      setExpandedPosition(null);
      setExpandedOrder(null);
    }
  }, [activeTab, expandedKey, filteredPositions, filteredOrders]);

  useEffect(() => {
    if (activeTab !== "orders" || !expandedKey) return;
    const fresh = sortedOrders.find((o) => String(o._id) === expandedKey);
    if (!fresh) return;
    if (
      fresh.status !== expandedOrder?.status ||
      Number(fresh.filledQuantity) !== Number(expandedOrder?.filledQuantity ?? 0)
    ) {
      setExpandedOrder(fresh);
    }
  }, [
    activeTab,
    expandedKey,
    sortedOrders,
    expandedOrder?.status,
    expandedOrder?.filledQuantity,
  ]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setExpandedKey(null);
    setExpandedPosition(null);
    setExpandedOrder(null);
  };

  const handleToggleExpand = (key, item) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      setExpandedPosition(null);
      setExpandedOrder(null);
      return;
    }
    setExpandedKey(key);
    if (activeTab === "holdings") {
      setExpandedPosition(item);
      setExpandedOrder(null);
    } else {
      setExpandedOrder(item);
      setExpandedPosition(null);
    }
  };

  const handleCancelRequest = (order) => {
    if (!order?._id || cancelBusyId) return;
    setOrderToCancel(order);
  };

  const closeCancelModal = () => {
    if (cancelBusyId) return;
    setOrderToCancel(null);
  };

  const confirmCancel = async () => {
    const orderId = orderToCancel?._id;
    if (!orderId || cancelBusyId) return;

    const result = await dispatch(cancelBondPredictOrder(orderId));
    if (cancelBondPredictOrder.fulfilled.match(result)) {
      setToast({
        tone: "success",
        text: "Order cancelled. Unfilled COIN returned to your wallet.",
      });
      setOrderToCancel(null);
      await Promise.all([
        dispatch(fetchBondPredictOrders({ limit: FETCH_LIMIT, skip: 0 })),
        dispatch(fetchBondPredictPortfolio({ limit: FETCH_LIMIT, skip: 0 })),
      ]);
      dispatch(checkAppAuth());
    } else {
      setToast({
        tone: "error",
        text: result.payload || "Could not cancel order.",
      });
    }
  };

  const handleRowTrade = (side, rowItem) => {
    if (!user?._id) {
      setToast({ tone: "warn", text: "Sign in to place orders." });
      return;
    }
    const isHoldings = activeTab === "holdings";
    const target = resolvePortfolioTradeTarget(
      isHoldings ? "holdings" : "orders",
      isHoldings ? rowItem || expandedPosition : null,
      isHoldings ? null : rowItem || expandedOrder
    );
    if (!target?.market || !target?.outcome?._id) {
      setToast({ tone: "error", text: "Could not resolve market for this row." });
      return;
    }
    setTradeMarket(target.market);
    setTradeOutcome(target.outcome);
    setTradeInitialSide(side === ORDER_SIDES.SELL ? ORDER_SIDES.SELL : ORDER_SIDES.BUY);
    setTradeModalOpen(true);
  };

  const handleTradePlaced = async (toastPayload) => {
    if (toastPayload) setToast(toastPayload);
    await load();
    dispatch(checkAppAuth());
  };

  const openMarket = (id) => navigate(`/app/bond-predict/${id}`);

  const railProps = {
    activeTab,
    onTabChange: handleTabChange,
    search,
    onSearchChange: setSearch,
    holdingsFilters,
    onHoldingsFiltersChange: setHoldingsFilters,
    ordersFilters,
    onOrdersFiltersChange: setOrdersFilters,
    categoryOptions,
    positionsCount: sortedPositions.length,
    openOrdersCount,
    resultCount:
      activeTab === "holdings" ? filteredPositions.length : filteredOrders.length,
    pnl,
    pnlTone,
    invested,
    activeFilterCount,
    onClearFilters: handleClearFilters,
    collapsed: isFilterRailCollapsed,
    onToggleCollapse: toggleFilterRailWidth,
  };

  const loading =
    (activeTab === "holdings" && portfolioStatus === "loading" && !sortedPositions.length) ||
    (activeTab === "orders" && ordersStatus === "loading" && !sortedOrders.length);

  const renderCenter = () => {
    if (loading) {
      return (
        <div className="bp-port-state">
          <Loader2 size={28} className="bp-spin" />
          <p>Loading {activeTab === "holdings" ? "holdings" : "orders"}…</p>
        </div>
      );
    }

    if (activeTab === "holdings" && portfolioError && !sortedPositions.length) {
      return (
        <div className="bp-port-state bp-port-state-error">
          <CloudOff size={28} />
          <p>{portfolioError}</p>
          <button type="button" className="bp-primary-btn" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      );
    }

    if (activeTab === "holdings" && !filteredPositions.length) {
      return (
        <div className="bp-port-state">
          <Wallet size={32} aria-hidden />
          <h2>{sortedPositions.length ? "No matches" : "No positions yet"}</h2>
          <p>
            {sortedPositions.length
              ? "Try clearing search or changing the holdings filter."
              : "Place a trade on any market and your shares will show up in this desk."}
          </p>
          {!sortedPositions.length ? (
            <button type="button" className="bp-primary-btn" onClick={() => navigate(LIST_PATH)}>
              Browse markets
            </button>
          ) : null}
        </div>
      );
    }

    if (activeTab === "orders" && !filteredOrders.length) {
      return (
        <div className="bp-port-state">
          <Receipt size={32} aria-hidden />
          <h2>{sortedOrders.length ? "No matches" : "No orders yet"}</h2>
          <p>
            {sortedOrders.length
              ? "Try clearing search or changing the orders filter."
              : "Working, filled, and cancelled orders will appear here."}
          </p>
        </div>
      );
    }

    return activeTab === "holdings" ? (
      <PortfolioHoldingsTable
        positions={filteredPositions}
        expandedKey={expandedKey}
        onToggleExpand={handleToggleExpand}
        userId={user?._id}
        onTrade={handleRowTrade}
        onOpenMarket={openMarket}
      />
    ) : (
      <PortfolioOrdersTable
        orders={filteredOrders}
        expandedKey={expandedKey}
        onToggleExpand={handleToggleExpand}
        userId={user?._id}
        onTrade={handleRowTrade}
        onOpenMarket={openMarket}
        onCancelOrder={handleCancelRequest}
        cancelBusy={!!cancelBusyId}
      />
    );
  };

  return (
    <div
      className={`bp-page bp-page-portfolio${isFilterRailCollapsed ? " bp-filter-rail-collapsed" : ""}`}
      style={{ "--bp-filters-w": `${filterRailWidth}px`, "--floor-index-w": `${filterRailWidth}px` }}
    >
      {toast ? (
        <div className={`bp-toast bp-toast-${toast.tone || "info"}`} role="status">
          {toast.text}
        </div>
      ) : null}

      <div className="bp-desk-layout">
        <div className="floor-index-pane bp-rail-column" aria-label="Portfolio filters">
          <PortfolioFiltersRail {...railProps} />
          <div
            className="floor-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={filterRailWidth}
            aria-valuemin={filterRailMin}
            aria-valuemax={filterRailMax}
            aria-label="Resize portfolio filters"
            title="Drag to resize · double-click to reset"
            onPointerDown={handleFilterRailResizePointerDown}
            onDoubleClick={resetFilterRailWidth}
          />
        </div>

        <div className="bp-main-column">
          <PortfolioChrome
            activeTab={activeTab}
            onTabChange={handleTabChange}
            positionsCount={sortedPositions.length}
            openOrdersCount={openOrdersCount}
            search={search}
            onSearchChange={setSearch}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            onMarkets={() => navigate(LIST_PATH)}
            filtersOpen={filtersOpen}
            onFiltersToggle={() => setFiltersOpen((v) => !v)}
            activeFilterCount={activeFilterCount}
          />

          <div className="bp-desk-main">
            <div className="bp-board-wrap bp-port-board-wrap bp-board-wrap-solo">
              <main className="bp-board bp-port-board">{renderCenter()}</main>
            </div>
          </div>
        </div>
      </div>

      {filtersOpen ? (
        <div
          className="bp-filters-drawer-backdrop"
          onClick={() => setFiltersOpen(false)}
          role="presentation"
        >
          <div
            className="bp-filters-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Portfolio filters"
          >
            <PortfolioFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
          </div>
        </div>
      ) : null}

      <MarketDetailOrderModal
        open={tradeModalOpen}
        onClose={() => setTradeModalOpen(false)}
        market={tradeMarket}
        selectedOutcome={tradeOutcome}
        orderBook={orderBook}
        orderBookStatus={orderBookStatus}
        userCoin={user?.COIN}
        initialSide={tradeInitialSide}
        onPlaced={handleTradePlaced}
      />

      <PortfolioCancelModal
        preview={cancelPreview}
        busy={!!cancelBusyId}
        onClose={closeCancelModal}
        onConfirm={confirmCancel}
      />
    </div>
  );
};

export default BondPredictPortfolio;
